// Import controller interfaces
import {
    IPropertyController
} from '../Interfaces/ControllerInterfacesBarrel';
// Import api models.
import ApiController from './ApiController';
import {
    Address,
    AddressVerification,
    ErrorMessage,
    GpsCoordinatesWithAccuracy,
    PossibleState,
    ThirdPartyPropertyDataCommercial,
    ThirdPartyPropertyDataPersonal
} from '@app/models/api';

export default class ProductionPropertyController extends ApiController implements IPropertyController {

    private readonly controllerUrl: string = 'v2/property';

    public constructor() {
        super();
    }

    public async verifyAddress(addressLine1: string, addressLine2: string, city: string, state: string, postalCode: string): Promise<AddressVerification> {
        let url: string = `${this.controllerUrl}/address/verify-components`;
        let params: { [key: string]: string } = {
            addressLine1,
            addressLine2,
            city,
            state,
            postalCode,
        };

        try {
            let response: Response = await super.makeGetCall(url, undefined, params, false);
            return await response.json();
        }
        catch (error) {
            if (ErrorMessage.isErrorMessage(error)) {
                error.message = 'Unable to verify address.';
            }
            throw error;
        }
    }

    public async verifySingleLineAddress(address: string): Promise<AddressVerification> {
        let url: string = `${this.controllerUrl}/address/verify-single-line`;
        let params: { [key: string]: string } = { address };

        try {
            let response: Response = await super.makeGetCall(url, undefined, params, false);
            return await response.json();
        }
        catch (error) {
            if (ErrorMessage.isErrorMessage(error)) {
                error.message = 'Unable to verify address.';
            }
            throw error;
        }
    }

    public async geocodeAddress(addressLine1: string, addressLine2: string, city: string, state: string, postalCode: string): Promise<GpsCoordinatesWithAccuracy> {
        let url: string = `${this.controllerUrl}/address/locate`;
        let params: { [key: string]: string } = {
            addressLine1,
            addressLine2,
            city,
            state,
            postalCode,
        };

        try {
            let response: Response = await super.makeGetCall(url, undefined, params, true);
            return await response.json();
        }
        catch (error) {
            if (ErrorMessage.isErrorMessage(error)) {
                error.message = 'Unable to geocode address.';
            }
            throw error;
        }
    }

    public async getAddressSuggestions(address: string): Promise<Address[]> {
        let url: string = `${this.controllerUrl}/address/search`;
        let params: {[key: string]: string} = { address };

        try {
            let response: Response = await super.makeGetCall(url, undefined, params, false);
            return await response.json();
        }
        catch (error) {
            if (ErrorMessage.isErrorMessage(error)) {
                error.message = 'Could not get address suggestions.';
            }
            throw error;
        }
    }

    public async getStates(): Promise<PossibleState[]> {
        let url: string = `${this.controllerUrl}/states`;

        try {
            let response: Response = await super.makeGetCall(url, undefined, undefined, false);
            return await response.json();
        }
        catch (error) {
            if (ErrorMessage.isErrorMessage(error)) {
                error.message = 'Could not get states.';
            }
            throw error;
        }
    }

    public async getThirdPartyPropertyDataCommercial(address: string, city: string, state: string, postalCode: string, lat: number, lng: number): Promise<ThirdPartyPropertyDataCommercial> {
        let url: string = `${this.controllerUrl}/address/property-data/comm`;
        let params: { [key: string]: string } = {
            address,
            city,
            state,
            postalCode,
            'lat': lat.toString(),
            'lng': lng.toString(),
        };

        try {
            let response: Response = await super.makeGetCall(url, undefined, params, true);
            return await response.json();
        }
        catch (error) {
            if (ErrorMessage.isErrorMessage(error)) {
                error.message = 'Unable to fetch any third-party property data.';
            }
            throw error;
        }
    }

    public async getThirdPartyPropertyDataPersonal(addressLine1: string, addressLine2: string, city: string, state: string, postalCode: string, lat: number, lng: number): Promise<ThirdPartyPropertyDataPersonal> {
        let url: string = `${this.controllerUrl}/address/property-data/pers`;
        let params: { [key: string]: string } = {
            addressLine1,
            addressLine2,
            city,
            state,
            postalCode,
            'lat': lat.toString(),
            'lng': lng.toString(),
        };

        try {
            let response: Response = await super.makeGetCall(url, undefined, params, true);
            return await response.json();
        }
        catch (error) {
            if (ErrorMessage.isErrorMessage(error)) {
                error.message = 'Unable to fetch any third-party property data.';
            }
            throw error;
        }
    }
};